import React, { useEffect, useMemo, useState } from 'react';
import AdminTopBar from '../../components/AdminTopBar';
import AdminSidebar from '../../components/AdminSidebar';
import { API_BASE_URL } from '../../config';
import { normalizeAdminNotificationMeta } from '../../utils/adminNotificationMeta';

const ALLERGEN_LIST = ['Sut', 'Yumurta', 'Fistik', 'Soya', 'Bugday', 'Balik', 'Deniz Urunleri', 'Gluten', 'Bal'];

const VIEW = {
  CREATE: 'CREATE',
  HISTORY: 'HISTORY',
  TEMPLATES: 'TEMPLATES'
};

function getWorkState(notification) {
  const filters = notification?.targetFilters && typeof notification.targetFilters === 'object' ? notification.targetFilters : {};
  if (filters.resolvedAt) return 'resolved';
  if (filters.acknowledgedAt) return 'acknowledged';
  if (notification?.isRead) return 'read';
  return 'unread';
}

function toDateText(value) {
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch (_) {
    return '-';
  }
}

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetUserQuery, setTargetUserQuery] = useState('');
  const [selectedTargetUserIds, setSelectedTargetUserIds] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [assignTarget, setAssignTarget] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [viewTab, setViewTab] = useState(VIEW.CREATE);
  const [actionRequiredOnly, setActionRequiredOnly] = useState(false);
  const [templateSavingKey, setTemplateSavingKey] = useState('');
  const [pushTemplates, setPushTemplates] = useState([]);
  const [pushTemplateEdits, setPushTemplateEdits] = useState({});
  const [pushTemplatesLoading, setPushTemplatesLoading] = useState(false);
  const [pushTemplatesError, setPushTemplatesError] = useState('');

  const [filters, setFilters] = useState({
    eventType: 'ALL',
    eventAction: 'ALL',
    eventSeverity: 'ALL',
    eventTargetArea: 'ALL',
    workState: 'ALL',
    search: '',
    onlyMine: false
  });

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'ADMIN_BROADCAST',
    targetUserId: '',
    targetUserIds: [],
    targetFilters: {
      isPremium: false,
      hasBaby: false,
      emailVerified: false,
      excludeAdmins: true,
      allergies: []
    }
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/all`, {
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const items = (data.notifications || []).map(normalizeAdminNotificationMeta);
        setNotifications(items);
      }
    } catch (err) {
      console.error('admin notifications fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPushTemplates = async () => {
    try {
      setPushTemplatesLoading(true);
      setPushTemplatesError('');
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/push-templates`, {
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `push_templates_http_${res.status}`);
      }
      const data = await res.json();
      const templates = Array.isArray(data?.templates) ? data.templates : [];
      setPushTemplates(templates);
      const next = {};
      templates.forEach((item) => {
        next[item.key] = {
          title: item.title || '',
          body: item.body || ''
        };
      });
      setPushTemplateEdits(next);
    } catch (err) {
      console.error('push templates fetch failed', err);
      setPushTemplates([]);
      setPushTemplatesError(String(err?.message || 'push_templates_fetch_failed'));
    } finally {
      setPushTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchPushTemplates();
    (async () => {
      try {
        const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        const [assigneesRes, meRes, usersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/notifications/admin/assignees`, {
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          }),
          fetch(`${API_BASE_URL}/auth/admin/me`, {
            credentials: 'include',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          }),
          fetch(`${API_BASE_URL}/api/admin/users`, {
            credentials: 'include',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          })
        ]);
        if (assigneesRes.ok) {
          const data = await assigneesRes.json();
          setAssignees(Array.isArray(data?.admins) ? data.admins : []);
        }
        if (meRes.ok) {
          const me = await meRes.json();
          const id = Number(me?.id);
          if (Number.isInteger(id)) setCurrentAdminId(id);
        }
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          const users = Array.isArray(usersData?.users) ? usersData.users : [];
          setTargetUsers(users);
        }
      } catch (_) {
        // no-op
      }
    })();
  }, []);

  const handleTemplateChange = (templateKey, field, value) => {
    setPushTemplateEdits((prev) => ({
      ...prev,
      [templateKey]: {
        ...(prev[templateKey] || {}),
        [field]: value
      }
    }));
  };

  const handleTemplateSave = async (templateKey) => {
    const payload = pushTemplateEdits[templateKey];
    if (!payload) return;
    try {
      setTemplateSavingKey(templateKey);
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/push-templates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {})
        },
        body: JSON.stringify({
          templates: {
            [templateKey]: payload
          }
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.details || data.error || 'template_save_failed');
      }
      await fetchPushTemplates();
      alert('Push şablonu kaydedildi.');
    } catch (err) {
      alert(`Sablon kaydedilemedi: ${String(err?.message || '')}`);
    } finally {
      setTemplateSavingKey('');
    }
  };

  const targetUserOptions = useMemo(() => {
    const q = String(targetUserQuery || '').trim().toLowerCase();
    const onlyPremium = !!formData?.targetFilters?.isPremium;
    const onlyVerified = !!formData?.targetFilters?.emailVerified;
    const excludeAdmins = !!formData?.targetFilters?.excludeAdmins;
    const base = targetUsers.filter((u) => {
      if (onlyPremium && !u?.isPremium) return false;
      if (onlyVerified && !u?.emailVerified) return false;
      if (excludeAdmins && u?.isAdmin) return false;
      return true;
    });
    if (!q) return base.slice(0, 50);
    return base.filter((u) => {
      const txt = `${u?.name || ''} ${u?.email || ''} ${u?.id || ''}`.toLowerCase();
      return txt.includes(q);
    }).slice(0, 50);
  }, [targetUsers, targetUserQuery, formData?.targetFilters?.isPremium, formData?.targetFilters?.emailVerified, formData?.targetFilters?.excludeAdmins]);

  const selectedTargetUsers = useMemo(() => {
    const setIds = new Set(selectedTargetUserIds.map((x) => Number(x)));
    return targetUsers.filter((u) => setIds.has(Number(u.id)));
  }, [targetUsers, selectedTargetUserIds]);

  const addTargetUser = (rawId) => {
    const id = Number(rawId);
    if (!Number.isInteger(id)) return;
    setSelectedTargetUserIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      setFormData((p) => ({ ...p, targetUserIds: next, targetUserId: '' }));
      return next;
    });
  };

  const removeTargetUser = (rawId) => {
    const id = Number(rawId);
    setSelectedTargetUserIds((prev) => {
      const next = prev.filter((x) => Number(x) !== id);
      setFormData((p) => ({ ...p, targetUserIds: next }));
      return next;
    });
  };

  const stats = useMemo(() => {
    const total = notifications.length;
    const critical = notifications.filter((n) => n.eventSeverity === 'CRITICAL').length;
    const actionRequired = notifications.filter((n) => n.eventSeverity === 'ACTION_REQUIRED').length;
    return { total, critical, actionRequired };
  }, [notifications]);

  const uniqueOptions = useMemo(() => {
    const eventType = [...new Set(notifications.map((n) => n.eventType).filter(Boolean))];
    const eventAction = [...new Set(notifications.map((n) => n.eventAction).filter(Boolean))];
    const eventTargetArea = [...new Set(notifications.map((n) => n.eventTargetArea).filter(Boolean))];
    return { eventType, eventAction, eventTargetArea };
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const q = String(filters.search || '').trim().toLowerCase();
    return notifications.filter((n) => {
      if (actionRequiredOnly && !(n.eventSeverity === 'ACTION_REQUIRED' || n.eventSeverity === 'CRITICAL')) return false;
      if (filters.eventType !== 'ALL' && n.eventType !== filters.eventType) return false;
      if (filters.eventAction !== 'ALL' && n.eventAction !== filters.eventAction) return false;
      if (filters.eventSeverity !== 'ALL' && n.eventSeverity !== filters.eventSeverity) return false;
      if (filters.eventTargetArea !== 'ALL' && n.eventTargetArea !== filters.eventTargetArea) return false;
      if (filters.workState !== 'ALL' && getWorkState(n) !== filters.workState) return false;
      if (filters.onlyMine) {
        const assignedTo = Number(n?.targetFilters?.assignedToUserId || 0);
        if (!currentAdminId || assignedTo !== currentAdminId) return false;
      }
      if (!q) return true;
      const haystack = `${n.title || ''} ${n.message || ''} ${n.eventType || ''} ${n.eventAction || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [notifications, filters, actionRequiredOnly, currentAdminId]);

  const assigneeNameById = useMemo(() => {
    const out = {};
    assignees.forEach((a) => {
      out[Number(a.id)] = a.name || a.email || `Admin #${a.id}`;
    });
    return out;
  }, [assignees]);

  const allSelected = filteredNotifications.length > 0 && selectedIds.length === filteredNotifications.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(filteredNotifications.map((n) => n.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name.startsWith('filter_')) {
      const filterKey = name.replace('filter_', '');
      setFormData((prev) => ({
        ...prev,
        targetFilters: { ...prev.targetFilters, [filterKey]: checked }
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAllergyToggle = (allergy) => {
    setFormData((prev) => {
      const current = prev.targetFilters.allergies || [];
      const updated = current.includes(allergy) ? current.filter((a) => a !== allergy) : [...current, allergy];
      return { ...prev, targetFilters: { ...prev.targetFilters, allergies: updated } };
    });
  };

  const publishRefresh = () => {
    window.dispatchEvent(new CustomEvent('refreshNotifications'));
    window.dispatchEvent(new Event('realtime:notifications-updated'));
    try {
      const bc = new BroadcastChannel('notifications_channel');
      bc.postMessage('refresh');
      bc.close();
    } catch (_) {
      // no-op
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.details || data.error || 'create_failed');
      }
      alert('Bildirim yayinlandi.');
      setFormData({
        title: '',
        message: '',
        type: 'ADMIN_BROADCAST',
        targetUserId: '',
        targetUserIds: [],
        targetFilters: { isPremium: false, hasBaby: false, emailVerified: false, excludeAdmins: true, allergies: [] }
      });
      setSelectedTargetUserIds([]);
      setTargetUserQuery('');
      await fetchAll();
      publishRefresh();
    } catch (err) {
      alert(`Yayinlama hatasi: ${String(err?.message || '')}`);
    }
  };

  const withAdminAuth = () => {
    const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
    return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
  };

  const runBulkAction = async (path, successMessage) => {
    if (!selectedIds.length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...withAdminAuth()
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'bulk_action_failed');
      }
      alert(successMessage);
      setSelectedIds([]);
      await fetchAll();
      publishRefresh();
    } catch (err) {
      alert(`Islem basarisiz: ${String(err?.message || '')}`);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} bildirimi silmek istiyor musun?`)) return;
    await runBulkAction('bulk-delete', 'Secilen bildirimler silindi.');
  };

  const handleBulkRead = async () => {
    await runBulkAction('bulk-read', 'Secilen bildirimler okundu olarak isaretlendi.');
  };

  const handleBulkAcknowledge = async () => {
    await runBulkAction('bulk-acknowledge', 'Secilen bildirimler acknowledge edildi.');
  };

  const handleBulkAssign = async () => {
    if (!selectedIds.length) return;
    const assignedToUserId = assignTarget === '' ? null : Number(assignTarget);
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/bulk-assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...withAdminAuth()
        },
        body: JSON.stringify({ ids: selectedIds, assignedToUserId })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'bulk_assign_failed');
      }
      alert(assignedToUserId ? 'Secilen bildirimler atandi.' : 'Secilen bildirimlerin atamasi kaldirildi.');
      setSelectedIds([]);
      await fetchAll();
      publishRefresh();
    } catch (err) {
      alert(`Atama basarisiz: ${String(err?.message || '')}`);
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50 dark:bg-slate-950">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative md:ml-[var(--admin-sidebar-width,18rem)] transition-[margin] duration-300">
        <AdminTopBar onToggleMobile={() => setMobileSidebarOpen(true)} title="Bildirim ve Guvenlik Yonetimi" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-8 max-w-7xl mx-auto w-full">
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-slate-100 dark:border-slate-800 p-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewTab(VIEW.CREATE)}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${
                  viewTab === VIEW.CREATE
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : 'text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                Yeni Ilan Olustur
              </button>
              <button
                type="button"
                onClick={() => setViewTab(VIEW.HISTORY)}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${
                  viewTab === VIEW.HISTORY
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : 'text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                Gecmis Bildirimler
              </button>
              <button
                type="button"
                onClick={() => setViewTab(VIEW.TEMPLATES)}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${
                  viewTab === VIEW.TEMPLATES
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : 'text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                Push Sablonlari
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
              <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Toplam</p>
              <h3 className="text-3xl font-black text-slate-800 dark:text-white">{stats.total}</h3>
            </div>
            <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border-l-4 border-orange-500">
              <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Aksiyon Gereken</p>
              <h3 className="text-3xl font-black text-orange-500">{stats.actionRequired}</h3>
            </div>
            <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border-l-4 border-red-500">
              <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Kritik</p>
              <h3 className="text-3xl font-black text-red-500">{stats.critical}</h3>
            </div>
          </div>

          {viewTab === VIEW.CREATE && (
            <div className="bg-white dark:bg-[#1e293b] rounded-3xl p-8 shadow-xl border border-slate-100 dark:border-slate-800">
              <h3 className="text-lg font-black text-slate-800 dark:text-white mb-6 uppercase flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">campaign</span> Yeni Ilan Olustur
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <input
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-5 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-primary outline-none"
                  placeholder="Bildirim Basligi"
                />
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-5 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-primary outline-none"
                  placeholder="Detayli mesaj icerigi"
                />

                <div className="grid grid-cols-2 gap-4">
                  <select name="type" value={formData.type} onChange={handleChange} className="px-4 py-3 rounded-2xl border dark:bg-slate-900 dark:text-white">
                    <option value="ADMIN_BROADCAST">Genel Yayin</option>
                    <option value="TARGETED">Hedefli</option>
                    <option value="IKM_ALERT">Sistem Uyarisi</option>
                  </select>
                  <div className="space-y-2">
                    <input
                      value={targetUserQuery}
                      onChange={(e) => setTargetUserQuery(e.target.value)}
                      placeholder="Kullanici ara (isim / email)"
                      className="w-full px-4 py-3 rounded-2xl border dark:bg-slate-900 dark:text-white"
                    />
                    <div className="flex items-center gap-2">
                      <select
                        value=""
                        onChange={(e) => addTargetUser(e.target.value)}
                        className="w-full px-4 py-3 rounded-2xl border dark:bg-slate-900 dark:text-white"
                      >
                        <option value="">Tum kullanicilar veya secerek ekle</option>
                        {targetUserOptions.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || '(isimsiz)'} - {u.email} (#{u.id})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTargetUserIds([]);
                          setFormData((p) => ({ ...p, targetUserIds: [], targetUserId: '' }));
                        }}
                        className="px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-200"
                      >
                        Temizle
                      </button>
                    </div>
                    {selectedTargetUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedTargetUsers.map((u) => (
                          <button
                            type="button"
                            key={u.id}
                            onClick={() => removeTargetUser(u.id)}
                            className="px-3 py-1.5 rounded-full text-xs font-black bg-primary/10 text-primary border border-primary/30"
                            title="Listeden cikar"
                          >
                            {u.name || u.email} ×
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Secili hedef: <span className="font-black text-primary">{selectedTargetUsers.length || 0}</span>
                    </p>
                  </div>
                </div>

                {formData.type === 'TARGETED' && (
                  <div className="p-6 bg-primary/5 rounded-3xl border border-primary/10">
                    <h4 className="text-xs font-black text-primary uppercase mb-4">Hedefleme</h4>
                    <div className="flex flex-wrap gap-6 mb-6">
                      <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-slate-600 dark:text-slate-300">
                        <input type="checkbox" name="filter_isPremium" checked={formData.targetFilters.isPremium} onChange={handleChange} className="size-5 accent-primary" /> Premium
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-slate-600 dark:text-slate-300">
                        <input type="checkbox" name="filter_hasBaby" checked={formData.targetFilters.hasBaby} onChange={handleChange} className="size-5 accent-primary" /> Bebekli
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-slate-600 dark:text-slate-300">
                        <input type="checkbox" name="filter_emailVerified" checked={formData.targetFilters.emailVerified} onChange={handleChange} className="size-5 accent-primary" /> Email dogrulanmis
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-slate-600 dark:text-slate-300">
                        <input type="checkbox" name="filter_excludeAdmins" checked={formData.targetFilters.excludeAdmins} onChange={handleChange} className="size-5 accent-primary" /> Adminleri haric tut
                      </label>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
                      Eslesen kullanici: <span className="font-black text-primary">{targetUserOptions.length}</span> (ilk 50 listelenir)
                    </p>
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Alerjen filtreleri</p>
                    <div className="flex flex-wrap gap-2">
                      {ALLERGEN_LIST.map((a) => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => handleAllergyToggle(a)}
                          className={`px-4 py-1.5 rounded-full text-xs font-black transition-all ${
                            formData.targetFilters.allergies.includes(a)
                              ? 'bg-primary text-white shadow-lg shadow-primary/30'
                              : 'bg-white dark:bg-slate-800 text-slate-400'
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button type="submit" className="w-full py-4 bg-primary text-white font-black rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-primary/20">
                  YAYINLA
                </button>
              </form>
            </div>
          )}

          {viewTab === VIEW.HISTORY && (
            <div className="bg-white dark:bg-[#1e293b] rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 flex flex-col max-h-[780px] overflow-hidden">
              <div className="p-6 border-b border-slate-100 dark:border-slate-800 space-y-4">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="size-5 accent-primary cursor-pointer" />
                    <h3 className="text-lg font-black dark:text-white uppercase tracking-tighter">Gecmis Bildirimler</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleBulkRead} disabled={!selectedIds.length} className="px-3 py-2 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 disabled:opacity-40">Toplu Okundu</button>
                    <button onClick={handleBulkAcknowledge} disabled={!selectedIds.length} className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 disabled:opacity-40">Toplu Acknowledge</button>
                    <select value={assignTarget} onChange={(e) => setAssignTarget(e.target.value)} className="px-2 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-xs">
                      <option value="">Atama kaldir</option>
                      {assignees.map((a) => (
                        <option key={a.id} value={a.id}>{a.name || a.email || `Admin #${a.id}`}</option>
                      ))}
                    </select>
                    <button onClick={handleBulkAssign} disabled={!selectedIds.length} className="px-3 py-2 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 disabled:opacity-40">Toplu Ata</button>
                    <button onClick={handleBulkDelete} disabled={!selectedIds.length} className="px-3 py-2 rounded-lg text-xs font-bold bg-red-600 text-white disabled:opacity-40">Toplu Sil</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-2">
                  <select value={filters.eventType} onChange={(e) => setFilters((p) => ({ ...p, eventType: e.target.value }))} className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm">
                    <option value="ALL">Type: Hepsi</option>
                    {uniqueOptions.eventType.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <select value={filters.eventAction} onChange={(e) => setFilters((p) => ({ ...p, eventAction: e.target.value }))} className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm">
                    <option value="ALL">Action: Hepsi</option>
                    {uniqueOptions.eventAction.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <select value={filters.eventSeverity} onChange={(e) => setFilters((p) => ({ ...p, eventSeverity: e.target.value }))} className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm">
                    <option value="ALL">Severity: Hepsi</option>
                    <option value="INFO">INFO</option>
                    <option value="ACTION_REQUIRED">ACTION_REQUIRED</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                  <select value={filters.eventTargetArea} onChange={(e) => setFilters((p) => ({ ...p, eventTargetArea: e.target.value }))} className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm">
                    <option value="ALL">Area: Hepsi</option>
                    {uniqueOptions.eventTargetArea.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <select value={filters.workState} onChange={(e) => setFilters((p) => ({ ...p, workState: e.target.value }))} className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm">
                    <option value="ALL">State: Hepsi</option>
                    <option value="unread">Unread</option>
                    <option value="read">Read</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <input
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                    placeholder="Ara..."
                    className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:text-white text-sm"
                  />
                </div>

                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={actionRequiredOnly} onChange={(e) => setActionRequiredOnly(e.target.checked)} className="accent-primary" />
                  Sadece aksiyon gerekenler
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300 ml-4">
                  <input
                    type="checkbox"
                    checked={!!filters.onlyMine}
                    onChange={(e) => setFilters((p) => ({ ...p, onlyMine: e.target.checked }))}
                    className="accent-primary"
                  />
                  Sadece bana atananlar
                </label>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {loading ? (
                  <div className="h-40 flex items-center justify-center text-slate-400 font-black animate-pulse">Yukleniyor...</div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="h-40 flex items-center justify-center text-slate-400 font-black">Kayit bulunamadi.</div>
                ) : (
                  filteredNotifications.map((n) => (
                    <div key={n.id} className="group bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-primary/40 transition-all flex gap-4">
                      <div className="pt-1">
                        <input type="checkbox" checked={selectedIds.includes(n.id)} onChange={() => toggleSelect(n.id)} className="size-5 accent-primary cursor-pointer" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase ${
                              n.eventSeverity === 'CRITICAL'
                                ? 'bg-red-100 text-red-700'
                                : n.eventSeverity === 'ACTION_REQUIRED'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-blue-100 text-blue-700'
                            }`}>{n.eventType || n.type}</span>
                            <span className="text-[10px] font-black px-2 py-1 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">{n.eventAction || 'UNKNOWN'}</span>
                            <span className="text-[10px] font-black px-2 py-1 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">{getWorkState(n)}</span>
                          </div>
                        </div>

                        <h4 className="font-black text-slate-800 dark:text-white">{n.title}</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{n.message}</p>

                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                          <div>Area: <span className="font-semibold">{n.eventTargetArea || '-'}</span></div>
                          <div>Olusma: <span className="font-semibold">{toDateText(n.createdAt)}</span></div>
                          <div>
                            Atanan: <span className="font-semibold">
                              {Number(n?.targetFilters?.assignedToUserId)
                                ? (assigneeNameById[Number(n?.targetFilters?.assignedToUserId)] || `Admin #${n?.targetFilters?.assignedToUserId}`)
                                : '-'}
                            </span>
                          </div>
                          <div>
                            Ack: <span className="font-semibold">{n?.targetFilters?.acknowledgedAt ? toDateText(n?.targetFilters?.acknowledgedAt) : '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {viewTab === VIEW.TEMPLATES && (
            <div className="bg-white dark:bg-[#1e293b] rounded-3xl p-8 shadow-xl border border-slate-100 dark:border-slate-800 space-y-6">
              <div>
                <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">edit_notifications</span>
                  Push Sablonlari
                </h3>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Route, dedupe ve teknik alanlar kodda kalir. Buradan sadece mesaj basligi ve govdesini yonetirsin.
                </p>
                <p className="mt-2 text-xs font-bold text-slate-400">
                  Toplam sablon: {pushTemplates.length}
                </p>
              </div>

              {pushTemplatesLoading && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-6 text-sm text-slate-500 dark:text-slate-300">
                  Push sablonlari yukleniyor...
                </div>
              )}

              {!pushTemplatesLoading && pushTemplatesError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
                  Push sablonlari yuklenemedi: {pushTemplatesError}
                </div>
              )}

              {!pushTemplatesLoading && !pushTemplatesError && pushTemplates.length === 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                  Kayitli push sablonu bulunamadi.
                </div>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {pushTemplates.map((item) => {
                  const edited = pushTemplateEdits[item.key] || { title: '', body: '' };
                  return (
                    <div key={item.key} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-5 space-y-4">
                      <div>
                        <p className="text-xs font-black uppercase tracking-widest text-primary">{item.group}</p>
                        <h4 className="text-base font-black text-slate-800 dark:text-white">{item.label}</h4>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                        {Array.isArray(item.placeholders) && item.placeholders.length > 0 && (
                          <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                            Placeholderlar: {item.placeholders.map((p) => `{${p}}`).join(', ')}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <input
                          value={edited.title}
                          onChange={(e) => handleTemplateChange(item.key, 'title', e.target.value)}
                          className="w-full px-4 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 dark:text-white"
                          placeholder="Baslik"
                        />
                        <textarea
                          value={edited.body}
                          onChange={(e) => handleTemplateChange(item.key, 'body', e.target.value)}
                          rows="5"
                          className="w-full px-4 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 dark:text-white"
                          placeholder="Mesaj govdesi"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] text-slate-400 break-all">{item.key}</p>
                        <button
                          type="button"
                          onClick={() => handleTemplateSave(item.key)}
                          disabled={templateSavingKey === item.key}
                          className="px-4 py-2 rounded-xl bg-primary text-white text-xs font-black disabled:opacity-60"
                        >
                          {templateSavingKey === item.key ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
